import { useState } from "react";
import { ArrowDown, ArrowUpRight, Store, Handshake, HeartHandshake } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { SectionHeading } from "./SectionHeading";
import { Reveal } from "./Reveal";
import { ECOSYSTEM_FLOW, BRAND } from "@/data/content";
import { scrollToId } from "@/lib/scroll";

export function Ecosystem() {
  const [leaving, setLeaving] = useState(false);

  const goToStore = () => {
    setLeaving(true);
    setTimeout(() => { window.location.href = BRAND.storeUrl; }, 2400);
  };

  return (
    <section id="ecosystem" data-testid="ecosystem-section" className="py-24 lg:py-32 bg-pine-deep relative overflow-hidden grain">
      <div className="pointer-events-none absolute -top-52 right-0 h-[34rem] w-[34rem] rounded-full bg-gold/10 blur-3xl" />
      <div className="relative max-w-7xl mx-auto px-5 md:px-8 lg:px-12">
        <SectionHeading
          dark
          align="center"
          chapter="10"
          overline="The Ecosystem"
          title="Powered by the Dyntra Store Ecosystem"
          sub="Dyntra Store is an e-commerce platform focused on dental equipment, products and solutions for dental professionals and clinics. Dyntra Clinics extends this ecosystem into physical healthcare through its clinic franchise model."
        />

        <div className="mt-16 mx-auto max-w-2xl">
          {ECOSYSTEM_FLOW.map((step, i) => (
            <Reveal key={step.title} delay={i * 0.06}>
              <div className="flex flex-col items-center">
                <div
                  data-testid={`ecosystem-node-${i}`}
                  className={`w-full rounded-2xl border px-7 py-5 text-center backdrop-blur transition-all duration-500 hover:-translate-y-1 ${
                    i === 0
                      ? "bg-gold/15 border-gold/40"
                      : i === ECOSYSTEM_FLOW.length - 1
                        ? "bg-teal-brand/20 border-teal-brand/40"
                        : "bg-white/[0.05] border-ivory/15"
                  }`}
                >
                  <p className={`font-display text-lg md:text-xl font-semibold ${i === 0 ? "text-gold" : "text-ivory"}`}>{step.title}</p>
                  <p className="mt-1 text-xs md:text-sm text-ivory/60">{step.desc}</p>
                </div>
                {i < ECOSYSTEM_FLOW.length - 1 && (
                  <ArrowDown className="my-2.5 h-5 w-5 text-gold/70" />
                )}
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.2}>
          <div className="mt-14 flex flex-wrap items-center justify-center gap-4">
            <button
              data-testid="ecosystem-store-cta"
              onClick={goToStore}
              className="inline-flex items-center gap-2 rounded-full bg-gold px-7 py-4 text-sm font-bold text-pine-deep shadow-grand transition-all duration-300 hover:bg-gold-soft hover:-translate-y-0.5"
            >
              <Store className="h-4 w-4" /> Explore Dyntra Store <ArrowUpRight className="h-4 w-4" />
            </button>
            <button
              data-testid="ecosystem-franchise-cta"
              onClick={() => scrollToId("franchise")}
              className="inline-flex items-center gap-2 rounded-full border border-ivory/25 px-7 py-4 text-sm font-bold text-ivory transition-all duration-300 hover:bg-ivory/10"
            >
              <Handshake className="h-4 w-4" /> Explore Franchise Opportunity
            </button>
          </div>
        </Reveal>
      </div>

      <AnimatePresence>
        {leaving && (
          <motion.div
            data-testid="store-redirect-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] flex items-center justify-center bg-pine-deep grain"
          >
            <div className="text-center px-6">
              <motion.img
                src="/dyntra-logo.jpg"
                alt="Dyntra"
                className="mx-auto h-20 w-20 rounded-full object-cover ring-2 ring-gold/60"
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.5 }}
              />
              <motion.p
                initial={{ y: 16, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.25, duration: 0.6 }}
                className="mt-6 font-display text-2xl md:text-3xl font-medium text-ivory"
              >
                Taking you to <span className="italic text-gold">Dyntra Store</span>
              </motion.p>
              <motion.p
                initial={{ y: 16, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.5, duration: 0.6 }}
                className="mt-3 text-sm md:text-base text-ivory/70 max-w-md mx-auto flex items-center justify-center gap-2"
              >
                <HeartHandshake className="h-4 w-4 text-gold shrink-0" />
                Your e-commerce partner is excited to be part of your journey — very soon.
              </motion.p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
